/*
/!*
/!*
/!*
  This example requires some changes to your config:

  ```
  // tailwind.config.js
  module.exports = {
    // ...
    theme: {
      extend: {
        gridTemplateRows: {
          '[auto,auto,1fr]': 'auto auto 1fr',
        },
      },
    },
    plugins: [
      // ...
      require('@tailwindcss/aspect-ratio'),
    ],
  }
  ```
*!/
import { useState } from 'react'
import { StarIcon } from '@heroicons/react/20/solid'
import { RadioGroup } from '@headlessui/react'

const product = {
  name: 'Basic Tee 6-Pack',
  price: '$192',
  href: '#',
  breadcrumbs: [
    { id: 1, name: 'Men', href: '#' },
    { id: 2, name: 'Clothing', href: '#' },
  ],
  images: [
    {
      src: 'https://tailwindui.com/img/ecommerce-images/product-page-02-secondary-product-shot.jpg',
      alt: 'Two each of gray, white, and black shirts laying flat.',
    },
    {
      src: 'https://tailwindui.com/img/ecommerce-images/product-page-02-tertiary-product-shot-01.jpg',
      alt: 'Model wearing plain black basic tee.',
    },
    {
      src: 'https://tailwindui.com/img/ecommerce-images/product-page-02-tertiary-product-shot-02.jpg',
      alt: 'Model wearing plain gray basic tee.',
    },
    {
      src: 'https://tailwindui.com/img/ecommerce-images/product-page-02-featured-product-shot.jpg',
      alt: 'Model wearing plain white basic tee.',
    },
  ],
  colors: [
    { name: 'White', class: 'bg-white', selectedClass: 'ring-gray-400' },
    { name: 'Gray', class: 'bg-gray-200', selectedClass: 'ring-gray-400' },
    { name: 'Black', class: 'bg-gray-900', selectedClass: 'ring-gray-900' },
  ],
  sizes: [
    { name: 'XXS', inStock: false },
    { name: 'XS', inStock: true },
    { name: 'S', inStock: true },
    { name: 'M', inStock: true },
    { name: 'L', inStock: true },
    { name: 'XL', inStock: true },
    { name: '2XL', inStock: true },
    { name: '3XL', inStock: true },
  ],
  description:
    'The Basic Tee 6-Pack allows you to fully express your vibrant personality with three grayscale options. Feeling adventurous? Put on a heather gray tee. Want to be a trendsetter? Try our exclusive colorway: "Black". Need to add an extra pop of color to your outfit? Our white tee has you covered.',
  highlights: [
    'Hand cut and sewn locally',
    'Dyed with our proprietary colors',
    'Pre-washed & pre-shrunk',
    'Ultra-soft 100% cotton',
  ],
  details:
    'The 6-Pack includes two black, two white, and two heather gray Basic Tees. Sign up for our subscription service and be the first to get new, exciting colors, like our upcoming "Charcoal Gray" limited release.',
}
const reviews = { href: '#', average: 4, totalCount: 117 }

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function Profile() {
  const [selectedColor, setSelectedColor] = useState(product.colors[0])
  const [selectedSize, setSelectedSize] = useState(product.sizes[2])

  return (
    <div className="bg-white">
      <div className="pt-6">
        <nav aria-label="Breadcrumb">
          <ol role="list" className="mx-auto flex max-w-2xl items-center space-x-2 px-4 sm:px-6 lg:max-w-7xl lg:px-8">
            {product.breadcrumbs.map((breadcrumb) => (
              <li key={breadcrumb.id}>
                <div className="flex items-center">
                  <a href={breadcrumb.href} className="mr-2 text-sm font-medium text-gray-900">
                    {breadcrumb.name}
                  </a>
                  <svg
                    width={16}
                    height={20}
                    viewBox="0 0 16 20"
                    fill="currentColor"
                    aria-hidden="true"
                    className="h-5 w-4 text-gray-300"
                  >
                    <path d="M5.697 4.34L8.98 16.532h1.327L7.025 4.341H5.697z" />
                  </svg>
                </div>
              </li>
            ))}
            <li className="text-sm">
              <a href={product.href} aria-current="page" className="font-medium text-gray-500 hover:text-gray-600">
                {product.name}
              </a>
            </li>
          </ol>
        </nav>

        {/!* Image gallery *!/}
        <div className="mx-auto mt-6 max-w-2xl sm:px-6 lg:grid lg:max-w-7xl lg:grid-cols-3 lg:gap-x-8 lg:px-8">
          <div className="aspect-h-4 aspect-w-3 hidden overflow-hidden rounded-lg lg:block">
            <img
              src={product.images[0].src}
              alt={product.images[0].alt}
              className="h-full w-full object-cover object-center"
            />
          </div>
          <div className="hidden lg:grid lg:grid-cols-1 lg:gap-y-8">
            <div className="aspect-h-2 aspect-w-3 overflow-hidden rounded-lg">
              <img
                src={product.images[1].src}
                alt={product.images[1].alt}
                className="h-full w-full object-cover object-center"
              />
            </div>
            <div className="aspect-h-2 aspect-w-3 overflow-hidden rounded-lg">
              <img
                src={product.images[2].src}
                alt={product.images[2].alt}
                className="h-full w-full object-cover object-center"
              />
            </div>
          </div>
          <div className="aspect-h-5 aspect-w-4 lg:aspect-h-4 lg:aspect-w-3 sm:overflow-hidden sm:rounded-lg">
            <img
              src={product.images[3].src}
              alt={product.images[3].alt}
              className="h-full w-full object-cover object-center"
            />
          </div>
        </div>

        {/!* Product info *!/}
        <div className="mx-auto max-w-2xl px-4 pb-16 pt-10 sm:px-6 lg:grid lg:max-w-7xl lg:grid-cols-3 lg:grid-rows-[auto,auto,1fr] lg:gap-x-8 lg:px-8 lg:pb-24 lg:pt-16">
          <div className="lg:col-span-2 lg:border-r lg:border-gray-200 lg:pr-8">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">{product.name}</h1>
          </div>

          {/!* Options *!/}
          <div className="mt-4 lg:row-span-3 lg:mt-0">
            <h2 className="sr-only">Product information</h2>
            <p className="text-3xl tracking-tight text-gray-900">{product.price}</p>

            {/!* Reviews *!/}
            <div className="mt-6">
              <h3 className="sr-only">Reviews</h3>
              <div className="flex items-center">
                <div className="flex items-center">
                  {[0, 1, 2, 3, 4].map((rating) => (
                    <StarIcon
                      key={rating}
                      className={classNames(
                        reviews.average > rating ? 'text-gray-900' : 'text-gray-200',
                        'h-5 w-5 flex-shrink-0'
                      )}
                      aria-hidden="true"
                    />
                  ))}
                </div>
                <p className="sr-only">{reviews.average} out of 5 stars</p>
                <a href={reviews.href} className="ml-3 text-sm font-medium text-indigo-600 hover:text-indigo-500">
                  {reviews.totalCount} reviews
                </a>
              </div>
            </div>

            <form className="mt-10">
              {/!* Colors *!/}
              <div>
                <h3 className="text-sm font-medium text-gray-900">Color</h3>

                <RadioGroup value={selectedColor} onChange={setSelectedColor} className="mt-4">
                  <RadioGroup.Label className="sr-only">Choose a color</RadioGroup.Label>
                  <div className="flex items-center space-x-3">
                    {product.colors.map((color) => (
                      <RadioGroup.Option
                        key={color.name}
                        value={color}
                        className={({ active, checked }) =>
                          classNames(
                            color.selectedClass,
                            active && checked ? 'ring ring-offset-1' : '',
                            !active && checked ? 'ring-2' : '',
                            'relative -m-0.5 flex cursor-pointer items-center justify-center rounded-full p-0.5 focus:outline-none'
                          )
                        }
                      >
                        <RadioGroup.Label as="span" className="sr-only">
                          {color.name}
                        </RadioGroup.Label>
                        <span
                          aria-hidden="true"
                          className={classNames(
                            color.class,
                            'h-8 w-8 rounded-full border border-black border-opacity-10'
                          )}
                        />
                      </RadioGroup.Option>
                    ))}
                  </div>
                </RadioGroup>
              </div>

              {/!* Sizes *!/}
              <div className="mt-10">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-900">Size</h3>
                  <a href="#" className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
                    Size guide
                  </a>
                </div>

                <RadioGroup value={selectedSize} onChange={setSelectedSize} className="mt-4">
                  <RadioGroup.Label className="sr-only">Choose a size</RadioGroup.Label>
                  <div className="grid grid-cols-4 gap-4 sm:grid-cols-8 lg:grid-cols-4">
                    {product.sizes.map((size) => (
                      <RadioGroup.Option
                        key={size.name}
                        value={size}
                        disabled={!size.inStock}
                        className={({ active }) =>
                          classNames(
                            size.inStock
                              ? 'cursor-pointer bg-white text-gray-900 shadow-sm'
                              : 'cursor-not-allowed bg-gray-50 text-gray-200',
                            active ? 'ring-2 ring-indigo-500' : '',
                            'group relative flex items-center justify-center rounded-md border py-3 px-4 text-sm font-medium uppercase hover:bg-gray-50 focus:outline-none sm:flex-1 sm:py-6'
                          )
                        }
                      >
                        {({ active, checked }) => (
                          <>
                            <RadioGroup.Label as="span">{size.name}</RadioGroup.Label>
                            {size.inStock ? (
                              <span
                                className={classNames(
                                  active ? 'border' : 'border-2',
                                  checked ? 'border-indigo-500' : 'border-transparent',
                                  'pointer-events-none absolute -inset-px rounded-md'
                                )}
                                aria-hidden="true"
                              />
                            ) : (
                              <span
                                aria-hidden="true"
                                className="pointer-events-none absolute -inset-px rounded-md border-2 border-gray-200"
                              >
                                <svg
                                  className="absolute inset-0 h-full w-full stroke-2 text-gray-200"
                                  viewBox="0 0 100 100"
                                  preserveAspectRatio="none"
                                  stroke="currentColor"
                                >
                                  <line x1={0} y1={100} x2={100} y2={0} vectorEffect="non-scaling-stroke" />
                                </svg>
                              </span>
                            )}
                          </>
                        )}
                      </RadioGroup.Option>
                    ))}
                  </div>
                </RadioGroup>
              </div>

              <button
                type="submit"
                className="mt-10 flex w-full items-center justify-center rounded-md border border-transparent bg-indigo-600 px-8 py-3 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Add to bag
              </button>
            </form>
          </div>

          <div className="py-10 lg:col-span-2 lg:col-start-1 lg:border-r lg:border-gray-200 lg:pb-16 lg:pr-8 lg:pt-6">
            {/!* Description and details *!/}
            <div>
              <h3 className="sr-only">Description</h3>

              <div className="space-y-6">
                <p className="text-base text-gray-900">{product.description}</p>
              </div>
            </div>

            <div className="mt-10">
              <h3 className="text-sm font-medium text-gray-900">Highlights</h3>

              <div className="mt-4">
                <ul role="list" className="list-disc space-y-2 pl-4 text-sm">
                  {product.highlights.map((highlight) => (
                    <li key={highlight} className="text-gray-400">
                      <span className="text-gray-600">{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-10">
              <h2 className="text-sm font-medium text-gray-900">Details</h2>

              <div className="mt-4 space-y-6">
                <p className="text-sm text-gray-600">{product.details}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
*!/


import React from 'react';
import Masonry from 'react-masonry-css';

const product = {
  name: 'Basic Tee 6-Pack',
  price: '$192',
  href: '#',
  breadcrumbs: [
    { id: 1, name: 'Men', href: '#' },
    { id: 2, name: 'Clothing', href: '#' },
  ],
  images: [
    {
      src: 'https://tailwindui.com/img/ecommerce-images/product-page-02-secondary-product-shot.jpg',
      alt: 'Two each of gray, white, and black shirts laying flat.',
    },
    {
      src: 'https://tailwindui.com/img/ecommerce-images/product-page-02-tertiary-product-shot-01.jpg',
      alt: 'Model wearing plain black basic tee.',
    },
    {
      src: 'https://tailwindui.com/img/ecommerce-images/product-page-02-tertiary-product-shot-02.jpg',
      alt: 'Model wearing plain gray basic tee.',
    },
    {
      src: 'https://tailwindui.com/img/ecommerce-images/product-page-02-featured-product-shot.jpg',
      alt: 'Model wearing plain white basic tee.',
    },




      {
      src: 'https://tailwindui.com/img/ecommerce-images/product-page-02-secondary-product-shot.jpg',
      alt: 'Two each of gray, white, and black shirts laying flat.',
    },
    {
      src: 'https://tailwindui.com/img/ecommerce-images/product-page-02-tertiary-product-shot-01.jpg',
      alt: 'Model wearing plain black basic tee.',
    },
    {
      src: 'https://tailwindui.com/img/ecommerce-images/product-page-02-tertiary-product-shot-02.jpg',
      alt: 'Model wearing plain gray basic tee.',
    },
    {
      src: 'https://tailwindui.com/img/ecommerce-images/product-page-02-featured-product-shot.jpg',
      alt: 'Model wearing plain white basic tee.',
    },

  ],
  colors: [
    { name: 'White', class: 'bg-white', selectedClass: 'ring-gray-400' },
    { name: 'Gray', class: 'bg-gray-200', selectedClass: 'ring-gray-400' },
    { name: 'Black', class: 'bg-gray-900', selectedClass: 'ring-gray-900' },
  ],
  sizes: [
    { name: 'XXS', inStock: false },
    { name: 'XS', inStock: true },
    { name: 'S', inStock: true },
    { name: 'M', inStock: true },
    { name: 'L', inStock: true },
    { name: 'XL', inStock: true },
    { name: '2XL', inStock: true },
    { name: '3XL', inStock: true },
  ],
  description:
    'The Basic Tee 6-Pack allows you to fully express your vibrant personality with three grayscale options. Feeling adventurous? Put on a heather gray tee. Want to be a trendsetter? Try our exclusive colorway: "Black". Need to add an extra pop of color to your outfit? Our white tee has you covered.',
  highlights: [
    'Hand cut and sewn locally',
    'Dyed with our proprietary colors',
    'Pre-washed & pre-shrunk',
    'Ultra-soft 100% cotton',
  ],
  details:
    'The 6-Pack includes two black, two white, and two heather gray Basic Tees. Sign up for our subscription service and be the first to get new, exciting colors, like our upcoming "Charcoal Gray" limited release.',
}
const reviews = { href: '#', average: 4, totalCount: 117 }

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}


const Profile = () => {
/!*
   const breakpointColumnsObj = { default: 4 };
  const baseWidth = 903;
  const increment = 252;
  for (let i = 0; i < 3; i++) {
    breakpointColumnsObj[baseWidth + increment * i] = i + 1;
  }*!/

 const breakpointColumnsObj = {
    default: 9, // Number of columns for default layout, set to 9 to display all images in different columns
  1100: 8,   // Number of columns for screens at least 1100px wide
  768: 6,    // Number of columns for screens at least 768px wide
  640: 3,    // Number of columns for screens at least 640px wide (phones), set to 3 columns
  };

/!*
 const breakpointColumnsObj = {
    default: 3, // Number of columns for default layout (adjust as needed)
    1100: 2, // Number of columns for screens at least 1100px wide
    700: 1,  // Number of columns for screens at least 700px wide
  };
*!/





  return (
    /!*<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {product.images.map((image, index) => (
        <div
          key={index}
          className="relative group overflow-hidden bg-white rounded-lg shadow-md"
        >
          <img
            src={image.src}
            alt={image.src}
            className="w-full h-auto transition-transform transform group-hover:scale-105"
          />
          <div className="p-4">
            <p className="text-gray-800 font-semibold">{image.alt}</p>
            <p className="text-gray-600">mwalwama</p>
          </div>
        </div>
      ))}
    </div>*!/
  /!* <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {product.images.map((image, index) => (
        <div
          key={index}
          className="relative group overflow-hidden bg-white rounded-lg shadow-md hover:shadow-lg"
        >
          <img
            src={image.src}
            alt={image.src}
            className="w-full h-auto transition-transform transform group-hover:scale-105"
          />
        </div>
      ))}

    </div>*!/

 <div style={{  }}>
      <Masonry
        breakpointCols={breakpointColumnsObj}
        className="masonry-grid grid gap-4"
        columnClassName="masonry-grid_column"
      >
        {product.images.map((image, index) => (
          <div key={index} className="masonry-grid_item">
            <img
              src={image.src}
              alt={image.alt}
              className="h-auto rounded-lg shadow-md transition-transform transform hover:scale-105"
            />
          </div>
        ))}
      </Masonry>
    </div>
  );
};

export default Profile;
*!/


import React, { useState, useEffect } from "react";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import AuthService from "../services/auth/auth_service";
import API from "../services/api";
import PopupProduct from "../pop up not used/pop-product";
import {ClockLoader} from "react-spinners";

export default function Profile() {
  const [productImages, setProductImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch data from the API endpoint
    AuthService.loadProductsByCategoryAndSubCategory('All', 'All')
      .then((data) => {
        setProductImages(data.data.results);

       setTimeout(()=>{
          setLoading(false);
       },1000)
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setLoading(false);
      });
  }, []);

  return (
    <div className=" p-4">
      {loading ? (
        <div className="product-card   flex justify-center items-center mt-12" style={{top:0, bottom:0, left:0, right:0, position:'absolute'}} >
  <ClockLoader color="#3B82F6"  loading={loading} size={30} />
         {/!* <i style={{fontSize:'100px'}} className='bx bxs-t-shirt  bx-burst'></i>*!/}
</div>
      ) : (
        <ResponsiveMasonry className={'mt-10'} columnsCountBreakPoints={{ 300: 2, 500: 3, 700: 4, 900: 7 }}>
          <Masonry gutter="16px">
            {productImages.map((product, index) => (
              <div
                  onClick={()=>PopupProduct.show(product)}
                key={index}
                className="rounded-lg bg-white  overflow-hidden shadow-md transition-transform transform hover:scale-105"
              >
                <img
                  src={product.photo} // Use the 'photo' field from the API response
                  alt={product.name}
                  className="w-full h-auto"
                  style={{ borderRadius: "8px" }}
                />
              </div>
            ))}
          </Masonry>
        </ResponsiveMasonry>
      )}
    </div>
  );activeMenu
}
*/





/*

import React, { useState, useEffect, useCallback } from "react";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import AuthService from "../services/auth/auth_service";
import API from "../services/api";
import PopupProduct from "../pop up not used/pop-product";
import { ClockLoader } from "react-spinners";
import {useNavigate} from "react-router";
import {RadioGroup} from "@headlessui/react";
import {toast} from "react-toastify";








export default function View1({productImages}) {
  const navigate = useNavigate()
    const [search, showSearch] = useState(false)





  const product = {
  name: 'Basic Tee 6-Pack',
  price: '$192',
  href: '#',
  breadcrumbs: [
    { id: 1, name: 'Men', href: '#' },
    { id: 2, name: 'Clothing', href: '#' },
  ],
  images: [
    {
      src: 'https://tailwindui.com/img/ecommerce-images/product-page-02-secondary-product-shot.jpg',
      alt: '',
    },
    {
      src: 'https://tailwindui.com/img/ecommerce-images/product-page-02-tertiary-product-shot-01.jpg',
      alt: 'Model wearing plain black basic tee.',
    },
    {
      src: 'https://tailwindui.com/img/ecommerce-images/product-page-02-tertiary-product-shot-02.jpg',
      alt: 'Model wearing plain gray basic tee.',
    },
    {
      src: 'https://tailwindui.com/img/ecommerce-images/product-page-02-featured-product-shot.jpg',
      alt: 'Model wearing plain white basic tee.',
    },
  ],
  colors: [
    { name: 'White', class: 'bg-white', selectedClass: 'ring-gray-400' },
    { name: 'Gray', class: 'bg-gray-200', selectedClass: 'ring-gray-400' },
    { name: 'Black', class: 'bg-gray-900', selectedClass: 'ring-gray-900' },
  ],
  sizes: [
    { name: 'XXS', inStock: false },
    { name: 'XS', inStock: true },
    { name: 'S', inStock: true },
    { name: 'M', inStock: true },
    { name: 'L', inStock: true },
    { name: 'XL', inStock: true },
    { name: '2XL', inStock: true },
    { name: '3XL', inStock: true },
  ],
  description:
    'The Basic Tee 6-Pack allows you to fully express your vibrant personality with three grayscale options. Feeling adventurous? Put on a heather gray tee. Want to be a trendsetter? Try our exclusive colorway: "Black". Need to add an extra pop of color to your outfit? Our white tee has you covered.',
  highlights: [
    'Hand cut and sewn locally',
    'Dyed with our proprietary colors',
    'Pre-washed & pre-shrunk',
    'Ultra-soft 100% cotton',
  ],
  details:
    'The 6-Pack includes two black, two white, and two heather gray Basic Tees. Sign up for our subscription service and be the first to get new, exciting colors, like our upcoming "Charcoal Gray" limited release.',
}
const reviews = { href: '#', average: 4, totalCount: 117 }

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}


 /!* const navigate = useNavigate()
  const [productImages, setProductImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    if (!hasMore || loadingMore) {
      return;
    }

    setLoadingMore(true);

    /!*AuthService.loadProductsByCategoryAndSubCategory("All", "All", page)
      .then((data) => {
        if (page === 1) {

          setProductImages(data.data.results);
        } else {
          setProductImages((prevImages) => [...prevImages, ...data.data.results]);
        }

        if (!data.data.next) {
          setHasMore(false);
        }

        setPage(page + 1);
        setLoadingMore(false);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setLoadingMore(false);
      });
  };*!/

      AuthService.loadProductsByCategoryAndSubCategory("All", "All", page)
  .then((data) => {
    if (page === 1) {
      const formattedResults = data.data.results.map((product) => ({
        ...product,
        price: product.price.toLocaleString(),
      }));
      setProductImages(formattedResults);
    } else {
      const formattedNewResults = data.data.results.map((product) => ({
        ...product,
        price: product.price.toLocaleString(),
      }));
      setProductImages((prevImages) => [...prevImages, ...formattedNewResults]);
    }

    if (!data.data.next) {
      setHasMore(false);
    }

    setPage(page + 1);
    setLoadingMore(false);
    setLoading(false);
  })
  .catch((error) => {
    console.error("Error fetching data:", error);
    setLoadingMore(false);
  });
};


  const handleScroll = useCallback(() => {
    if (
      window.innerHeight + window.scrollY >= document.body.offsetHeight - 100 &&
      !loadingMore &&
      hasMore
    ) {
      fetchData();
    }
  }, [loadingMore, hasMore]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);



    const handleClick = () => {
    const scrollPosition = window.scrollY;
    console.log('Scroll Position:', scrollPosition);
    sessionStorage.setItem('scroll', scrollPosition)
    // Add your logic to handle the scroll position here
  }*!/


  const CustomToast = ({ name, price, imageSrc }) => (
  <div className="bg-white p-4 flex items-center space-x-4">
    <div className="w-16 h-16 rounded-full">
      <img src={imageSrc} height={'100%'} alt="Product Image" />
    </div>
    <div className="flex-grow">
      <div style={{width:'100%'}} className="text-gray-700 text-lg font-semibold">
          Umeweka kikamilifu <i className={'text-red-500'}>{name}</i> kwenye kikapu na bei yake ni <i className={'text-green-500'}>{price} Tsh</i>
      </div>
    </div>
  </div>
);




  const categories = [
      'Tafuta hapa...',
  'Watch',
  'Shoes',
  'Mask',
  '7',
  'Books',
];



  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Tafuta hapa...");




  /!*const [suggestions, setSuggestions] = useState([]);

  const handleInputChange = async (event) => {
    const newQuery = event.target.value;
    setQuery(newQuery);

    // Make an API request to your backend to get search suggestions
    try {
      // Replace '/api/search' with your actual backend API endpoint
      const response = await fetch(`/api/search?s=${newQuery}`);
      const data = await response.json();
      setSuggestions(data.suggestions);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };*!/






const [filteredProducts, setFilteredProducts] = useState(productImages);

  const handleInputChange = (event) => {
    setQuery(event.target.value);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

 const handleSearch = () => {
  console.log("Search Button Clicked");
  console.log("Selected Category:", selectedCategory);
  console.log("Query:", query);
 /!* alert('Sorry ninaendelea kufanyia maboresho eneo hili keep expore and order more product')*!/

  // Log the product data for debugging
  console.log("Product Images:", productImages);

  // Perform the search based on the selectedCategory and query
  let filtered = productImages;

  if (selectedCategory !== "Tafuta hapa...") {
    console.log("Applying Category Filter");
    filtered = filtered.filter(
      (product) => product.category === selectedCategory
    );
  }

  if (query.trim() !== "") {
    console.log("Applying Query Filter");
    filtered = filtered.filter((product) =>
      product.name.toLowerCase().includes(query.toLowerCase())
    );
  }

  console.log("Filtered Products:", filtered);

  setFilteredProducts(filtered);
  showSearch(false);
};



         window.history.pushState(null, null, window.location.href);
window.onpopstate = function () {
  // Show an alert when the user clicks the back button
  /!*alert("Your click browser back button");*!/
 showSearch(false)

}



  return (
   /!* <div className="p-4" style={{marginBottom:'500px'}}>
      <ResponsiveMasonry
        className={"mt-10"}
        columnsCountBreakPoints={{ 300: 2, 500: 3, 700: 4, 900: 7 }}
      >
        <Masonry gutter="16px">
          {productImages.map((product, index) => (
            <div
              onClick={() => PopupProduct.show(product)}
              key={index}
              className="rounded-lg bg-white  overflow-hidden shadow-md transition-transform transform hover:scale-105"
            >
              <img
                src={product.photo}
                alt={product.name}
                className="w-full h-auto"
                style={{ borderRadius: "8px" }}
              />
            </div>
          ))}
        </Masonry>
      </ResponsiveMasonry>

      {loadingMore && (
        <div className="flex justify-center mt-4">
          <ClockLoader color="#3B82F6" size={30} />
        </div>
      )}
    </div>*!/

     /!*<div className="p-4" style={{ marginBottom: '500px' }}>
  <ResponsiveMasonry
    className={'mt-10'}
    columnsCountBreakPoints={{ 300: 2, 500: 3, 700: 4, 900: 7 }}
  >
    <Masonry gutter="16px">
      {productImages.map((product, index) => (
        <div
          key={index}
          className="relative group"
          style={{ cursor: 'pointer' }}
        >
          <div
            className="absolute top-0 left-0 right-0 bottom-0 p-2 bg-opacity-80 backdrop-blur backdrop-filter opacity-0 transition-opacity group-hover:opacity-100"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 2,
              borderRadius: '8px', // Match the border radius
              opacity: 0.9, // Reduce opacity
            }}
          >
            <p className="text-sm font-medium text-gray-700">{product.name}</p>
            <p className="text-sm font-medium text-indigo-600">{product.price}</p>
          </div>
          <img
            src={product.photo}
            alt={product.name}
            className="w-full h-auto rounded-lg"
            style={{ borderRadius: '8px', zIndex: 1 }}
          />
        </div>
      ))}
    </Masonry>
  </ResponsiveMasonry>

  {loadingMore && (
    <div className="flex justify-center mt-4">
      <ClockLoader color="#3B82F6" size={30} />
    </div>
  )}
</div>*!/



/!*<div className="p-4 " style={{ marginBottom: '500px' }}>
  <ResponsiveMasonry
    className={'mt-10'}
    columnsCountBreakPoints={{ 300: 2, 500: 3, 700: 4, 900: 7 }}
  >
    <Masonry gutter="16px">
      {productImages.map((product, index) => (
        <div
          onClick={() => PopupProduct.show(product)}
          key={index}
          className="relative bg-white group transform transition-transform hover:scale-105"
          style={{
            cursor: 'pointer',
            borderRadius: '8px', // Match the border radius of the real div
          }}
        >
                   <div
            className="absolute top-0 left-0 right-0 bottom-0 p-2 bg-opacity-25 backdrop-blur backdrop-filter opacity-0 transition-opacity group-hover:opacity-75"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 2,
              borderRadius: '8px', // Match the border radius
            }}
          >
            <p className="text-sm font-medium text-gray-700">{product.name}</p>
            <p className="text-sm font-medium text-indigo-600">{product.price}</p>
          </div>

          <img
            src={product.photo}
            alt={product.name}
            className="w-full h-auto rounded-lg"
            style={{ borderRadius: '8px', zIndex: 1 }}
          />
        </div>
      ))}
    </Masonry>
  </ResponsiveMasonry>

  {loadingMore && (
    <div className="flex justify-center mt-4">
      <ClockLoader color="#3B82F6" size={30} />
    </div>
  )}
</div>*!/



      <div className="sm:p-8" style={{ marginBottom: '150px' }}>
  <ResponsiveMasonry
    className={'sm:mt-2 mt-1 lg:mt-0'}
    columnsCountBreakPoints={{ 300: 2, 500: 3, 700: 4, 900: 7 }}
  >
    <Masonry gutter="16px">
      {productImages.map((d, index) => (
        <div
             onClick={()=>PopupProduct.show(d)}
         /!* onClick={()=>{navigate(`/popup?d=${encodeURIComponent(JSON.stringify(d))}`); /!*handleClick()*!/}}*!/

          key={d.id}
          className="relative bg-white group transform transition-transform hover:scale-105 rounded-lg overflow-hidden  shadow-md"
          style={{
            borderRadius: '8px', // Match the border radius of the real div
          }}
        >
          <div
  className="absolute top-0 left-0 right-0 bottom-0 flex flex-col justify-center items-center p-2 bg-opacity-80 backdrop-blur backdrop-filter opacity-0 transition-opacity group-hover:opacity-80"
  style={{
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    zIndex: 2,
    borderRadius: '8px', // Match the border radius
  }}
>
    <h3 className="products__price text-center font-bold text-gray-900">{d.name}</h3>
    <p className="products__price text-xl sm:text-sm md:text-xl lg:text-2xl  text-center font-semibold leading-5 text-gray-800">
  {d.price} Tsh
</p>

</div>
          {/!*<img
            src={product.photo}
            alt={product.name}
            className="w-full h-auto rounded-lg"
            style={{
              borderRadius: '8px', // Match the border radius
              zIndex: 1,
            }}
          />*!/}


          <img
  src={d.photo}
  alt={d.name}
  className="w-full h-auto rounded-lg filter brightness-110"
  style={{
    borderRadius: '8px', // Match the border radius
    zIndex: 1,
     filter: 'brightness(1.1) contrast(1.1) saturate(1.1)'
  }}
/>

        </div>
      ))}
    </Masonry>
  </ResponsiveMasonry>

  {/!*{loadingMore && (
    <div className="flex justify-center mt-4">
      <ClockLoader color="#3B82F6" size={30} />
    </div>
  )}*!/}


        <div onClick={()=>showSearch(true)} data-dial-init className="fixed bx-burst z-90 right-6 bottom-20 group">
          <div id="speed-dial-menu-default" className="flex hidden flex-col items-center mb-4 space-y-2">

          </div>
          <button type="button" data-dial-toggle="speed-dial-menu-default" aria-controls="speed-dial-menu-default"
                  aria-expanded="false"
                  className="flex justify-center items-center w-10 h-10 lg:w-14 lg:h-14 text-white bg-blue-700 rounded-full hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 focus:outline-none dark:focus:ring-blue-800">
           {/!* <svg aria-hidden="true" className="lg:w-8 lg:h-8 w-6 h-6 transition-transform group-hover:rotate-45" fill="none"
                 stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>*!/}
            <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-5.2-5.2M15 9a6 6 0 11-12 0 6 6 0 0112 0z"
                    />
                  </svg>
            <span className="sr-only">Open actions menu</span>
          </button>
        </div>



 {search && (
        <div className="fixed inset-0 flex justify-end items-start bg-gray-800 bg-opacity-50">
          <div className="p-4 bg-blue-100 w-full max-w-screen rounded-md shadow-lg animate-slideInFromTopRight">
            <h2 className="text-center font-bold text-gray-800">Tafuta hapa...</h2>
            <p className="text-center mt-3 font-bold text-red-800">
              *Chagua size unayohitaji*
            </p>

            <div className="mt-5 md:mt-8 w-full flex items-center justify-between">
              <div className="flex items-center w-5/6">
                <select
                  value={selectedCategory}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="text-sm font-bold text-center rounded border-2 border-purple-700 text-gray-600  w-full py-2 pl-5 pr-10 bg-white hover:border-gray-400 focus:outline-none appearance-none"
                >
                  {categories.map((category, index) => (
                    <option key={index} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              <div className="ml-2">
                <button
                  onClick={handleSearch}
                  className="border border-gray-800 text-base font-medium text-gray-800 px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-800 bg-gray-800 hover:bg-gray-900 text-white"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-5.2-5.2M15 9a6 6 0 11-12 0 6 6 0 0112 0z"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}







</div>



  );
}
*/




import React, { useState, useEffect } from "react";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import PopupProduct from "../pop up not used/pop-product";
import AuthService from "../services/auth/auth_service";

export default function View1({ productImages }) {
  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Tafuta hapa...");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const categories = ["Tafuta hapa...", "Watch", "Shoes", "Mask", "7", "Books"];
   const [search, showSearch] = useState(false)

  useEffect(() => {
    filterProducts();
  }, [query, selectedCategory, productImages]);

  const filterProducts = () => {
    let filtered = [...productImages];

    if (selectedCategory !== "Tafuta hapa...") {
      filtered = filtered.filter(
        (product) => product.category === selectedCategory
      );
    }

    if (query.trim() !== "") {
      filtered = filtered.filter((product) =>
        product.name.toLowerCase().includes(query.toLowerCase())
      );
    }

    console.log("Selected Category:", selectedCategory); // Log the selected category
    console.log("Query:", query); // Log the query
    console.log("Categories in Data:", [...new Set(productImages.map((product) => product.category))]); // Log the categories in your data

    setFilteredProducts(filtered);
  };

  const handleInputChange = (event) => {
    setQuery(event.target.value);
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };




         window.history.pushState(null, null, window.location.href);
window.onpopstate = function () {

 showSearch(false)

}



 const handleSearch = () => {
showSearch(false)
  }


  return (
    <div className="sm:p-8" style={{ marginBottom: "150px" }}>
      <div>
        {/*<input
          type="text"
          placeholder="Tafuta..."
          value={query}
          onChange={handleInputChange}
        />*/}
       {/* <select
          value={selectedCategory}
          onChange={handleCategoryChange}
        >
          {categories.map((category, index) => (
            <option key={index} value={category}>
              {category}
            </option>
          ))}
        </select>*/}
      </div>

      <ResponsiveMasonry
        className={"sm:mt-2 mt-1 lg:mt-0"}
        columnsCountBreakPoints={{ 300: 2, 500: 3, 700: 4, 900: 7 }}
      >
        <Masonry gutter="16px">
          {filteredProducts.map((product, index) => (
            <div
              onClick={() => PopupProduct.show(product)}
              key={product.id}
              className="relative bg-white group transform transition-transform hover:scale-105 rounded-lg overflow-hidden  shadow-md"
              style={{
                borderRadius: "8px",
              }}
            >
              <div
                className="absolute top-0 left-0 right-0 bottom-0 flex flex-col justify-center items-center p-2 bg-opacity-80 backdrop-blur backdrop-filter opacity-0 transition-opacity group-hover:opacity-80"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  zIndex: 2,
                  borderRadius: "8px",
                }}
              >
                <h3 className="products__price text-center font-bold text-gray-900">
                  {product.name.split('-')[0]}
                </h3>
                <p className="products__price text-xl sm:text-sm md:text-xl lg:text-2xl  text-center font-semibold leading-5 text-gray-800">
                  {product.price} Tsh
                </p>
              </div>
              <img
                src={product.photo}
                alt={product.name}
                className="w-full h-auto rounded-lg filter brightness-110"
                style={{
                  borderRadius: "8px",
                  zIndex: 1,
                  filter: "brightness(1.1) contrast(1.1) saturate(1.1)",
                }}
              />
            </div>
          ))}
        </Masonry>
      </ResponsiveMasonry>


       {search && (
        <div className="fixed inset-0 flex justify-end items-start bg-gray-800 bg-opacity-50">
          <div className="p-4 bg-blue-100 w-full max-w-screen rounded-md shadow-lg animate-slideInFromTopRight">
            <h2 style={{display:'hidden'}} className="text-center font-bold text-gray-800">Tafuta hapa...</h2>
            <p style={{display:'hidden'}} className="text-center mt-3 font-bold text-red-800">
              *Chagua size unayohitaji*
            </p>

            <div className="mt-5 md:mt-8 w-full flex items-center justify-between">
              <div className="flex items-center w-5/6">
               {/* <select
                  value={selectedCategory}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="text-sm font-bold text-center rounded border-2 border-purple-700 text-gray-600  w-full py-2 pl-5 pr-10 bg-white hover:border-gray-400 focus:outline-none appearance-none"
                >
                  {categories.map((category, index) => (
                    <option key={index} value={category}>
                      {category}
                    </option>
                  ))}
                </select>*/}

                <input
          type="text"
           className="text-sm font-bold text-center rounded border-2 border-purple-700 text-gray-600  w-full py-2 pl-5 pr-10 bg-white hover:border-gray-400 focus:outline-none appearance-none"
          placeholder="Tafuta..."
          value={query}
          onChange={handleInputChange}
        />
              </div>
              <div className="ml-2">
                <button
                  onClick={handleSearch}
                  className="border border-gray-800 text-base font-medium text-gray-800 px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-800 bg-gray-800 hover:bg-gray-900 text-white"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-5.2-5.2M15 9a6 6 0 11-12 0 6 6 0 0112 0z"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}





       <div onClick={()=>showSearch(true)} data-dial-init className="fixed bx-burst z-90 right-6 bottom-20 group">
          <div id="speed-dial-menu-default" className="flex hidden flex-col items-center mb-4 space-y-2">

          </div>
          <button type="button" data-dial-toggle="speed-dial-menu-default" aria-controls="speed-dial-menu-default"
                  aria-expanded="false"
                  className="flex justify-center items-center w-10 h-10 lg:w-14 lg:h-14 text-white bg-blue-700 rounded-full hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 focus:outline-none dark:focus:ring-blue-800">
           {/*{/!* <svg aria-hidden="true" className="lg:w-8 lg:h-8 w-6 h-6 transition-transform group-hover:rotate-45" fill="none"
                 stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>*!/}*/}
            <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-5.2-5.2M15 9a6 6 0 11-12 0 6 6 0 0112 0z"
                    />
                  </svg>
            <span className="sr-only">Open actions menu</span>
          </button>
        </div>


    </div>
  );
}









// Profile.js
/*
import React, { useState, useEffect, useCallback } from 'react';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';

import AuthService from '../services/auth/auth_service';
import PopupProduct from '../pop up not used/pop-product';
import ProductSearch from "../search/product-search";

const Profile = () => {
  const [productImages, setProductImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('All'); // Default category filter

  const fetchProducts = useCallback(() => {
    // Fetch data from the API endpoint with pagination and category filter
    AuthService.loadProductsByCategoryAndSubCategory(categoryFilter, 'All', page)
      .then((data) => {
        const newProducts = data.data.results;
        setProductImages((prevProducts) => [...prevProducts, ...newProducts]);
        setHasMore(newProducts.length > 0);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
        setLoading(false);
      });
  }, [categoryFilter, page]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleSearch = ({ category, subcategory }) => {
    setLoading(true);
    setProductImages([]);
    setPage(1);
    setHasMore(true);

    // Update the category filter based on the search
    setCategoryFilter(category);

    // Fetch data based on the category filter
    // Example:
    // AuthService.loadProductsByCategoryAndSubCategory(category, 'All')
    //   .then((data) => {
    //     setProductImages(data.data.results);
    //     setLoading(false);
    //   })
    //   .catch((error) => {
    //     console.error('Error fetching filtered data:', error);
    //     setLoading(false);
    //   });
  };

  const handleLoadMore = () => {
    if (hasMore) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  return (
    <div style={{marginTop:'400px'}}>
      <ProductSearch onSearch={handleSearch} />
      {loading ? (
        <div>Loading...</div>
      ) : (
        <div>
          <ResponsiveMasonry className={'mt-10'} columnsCountBreakPoints={{ 300: 2, 500: 3, 700: 4, 900: 7 }}>
            <Masonry gutter="16px">
              {productImages.map((product, index) => (
                <div
                  key={index}
                  className="rounded-lg bg-white  overflow-hidden shadow-md transition-transform transform hover:scale-105"
                  onClick={() => PopupProduct.show(product)}
                >
                  <img
                    src={product.photo}
                    alt={product.name}
                    className="w-full h-auto"
                    style={{ borderRadius: '8px' }}
                  />
                </div>
              ))}
            </Masonry>
          </ResponsiveMasonry>
          {hasMore && (
            <div className="mt-4 text-center">
              <button
                onClick={handleLoadMore}
                className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg"
              >
                Load More
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Profile;
*/












/*
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import AuthService from '../services/auth/auth_service';
import PopupProduct from '../pop up not used/pop-product';
import ProductSearch from "../search/product-search";
import CircleLoader from "react-spinners/CircleLoader";
import ClockLoader from "react-spinners/ClockLoader";

const Profile = () => {
  const [productImages, setProductImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('All'); // Default category filter
  const observer = useRef();

  const fetchProducts = useCallback(() => {
    // Fetch data from the API endpoint with pagination and category filter
    AuthService.loadProductsByCategoryAndSubCategory(categoryFilter, 'All', page)
      .then((data) => {
        const newProducts = data.data.results;
        setProductImages((prevProducts) => [...prevProducts, ...newProducts]);
        setHasMore(newProducts.length > 0);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
        setLoading(false);
      });
  }, [categoryFilter, page]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    // Create an Intersection Observer to detect when the user scrolls to the bottom
    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        setPage((prevPage) => prevPage + 1);
      }
    });

    // Attach the observer to the last element in the product list
    if (productImages.length > 0) {
      observer.current.observe(document.querySelector('.product-item:last-child'));
    }

    // Clean up the observer when unmounting
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [hasMore, productImages]);

  const handleSearch = ({ category, subcategory }) => {
    setLoading(true);
    setProductImages([]);
    setPage(1);
    setHasMore(true);

    // Update the category filter based on the search
    setCategoryFilter(category);
  };

  return (
    <div>
      <ProductSearch onSearch={handleSearch} />
      {loading ? (
        <div className="flex justify-center mt-4">
          <ClockLoader color="#3B82F6" size={30} />
        </div>
      ) : (
        <div>
          <ResponsiveMasonry className={'mt-10'} columnsCountBreakPoints={{ 300: 2, 500: 3, 700: 4, 900: 7 }}>
            <Masonry gutter="16px">
              {productImages.map((product, index) => (
                <div
                  key={index}
                  className="product-item rounded-lg bg-white overflow-hidden shadow-md transition-transform transform hover:scale-105"
                  onClick={() => PopupProduct.show(product)}
                >
                  <img
                    src={product.photo}
                    alt={product.name}
                    className="w-full h-auto"
                    style={{ borderRadius: '8px' }}
                  />
                </div>
              ))}
            </Masonry>
          </ResponsiveMasonry>
        </div>
      )}
      {hasMore && (
        <div className="flex justify-center mt-4">
          <ClockLoader color="#3B82F6" size={30} />
        </div>
      )}
    </div>
  );
};

export default Profile;
*/















/*
import React, { useState, useEffect, useCallback } from "react";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import AuthService from "../services/auth/auth_service";
import API from "../services/api";
import PopupProduct from "../pop up not used/pop-product";
import { ClockLoader } from "react-spinners";
import { useNavigate } from "react-router";

export default function Package() {
  const navigate = useNavigate();
  const [productImages, setProductImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Add a function to load more data
  const loadMoreData = () => {
   console.log("Load more data called");
  console.log("hasMore:", hasMore);
  console.log("loadingMore:", loadingMore);

  if (!hasMore || loadingMore) {
    console.log("Cannot load more data at the moment");
    return;
  }

  setLoadingMore(true);

    AuthService.loadProductsByCategoryAndSubCategory("All", "All", page)
      .then((data) => {
         console.log("API Response Data:", data);
          console.log("API Response Data:", data);

    // Log the results from the API response
    console.log("API Response Results:", data.data.results);

    // Check if the API response has a "next" value
    console.log("Has Next Page:", !!data.data.next);

        if (page === 1) {
          const formattedResults = data.data.results.map((product) => ({
            ...product,
            price: product.price.toLocaleString(),
          }));
          setProductImages(formattedResults);
        } else {
          const formattedNewResults = data.data.results.map((product) => ({
            ...product,
            price: product.price.toLocaleString(),
          }));
          setProductImages((prevImages) => [...prevImages, ...formattedNewResults]);
        }


        if (!data.data.next) {
          setHasMore(false);
        }
console.log("Page before increment:", page);
setPage(page + 1);
console.log("Page after increment:", page);
setLoadingMore(false); // Reset loadingMore state
setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setLoadingMore(false);
      });
  };

  // Add a function to handle the scroll event
  const handleScroll = () => {
  const scrollPosition = window.scrollY;
  const totalHeight = document.documentElement.scrollHeight;
  const windowHeight = window.innerHeight;

  console.log("Scroll Position:", scrollPosition); // Log the scroll position

  // Check if the user has scrolled to the bottom
  if (scrollPosition + windowHeight >= totalHeight) {
    console.log("Reached the bottom of the page"); // Log that the bottom is reached
    loadMoreData();
  }
};

// Add a useEffect to attach the scroll event listener
useEffect(() => {
  window.addEventListener("scroll", handleScroll);
  return () => {
    window.removeEventListener("scroll", handleScroll);
  };
}, []);


useEffect(() => {
  console.log("Initial page state:", page);
  loadMoreData();
}, []);


  const handleClick = () => {
    const scrollPosition = window.scrollY;
    console.log("Scroll Position:", scrollPosition);
    sessionStorage.setItem("scroll", scrollPosition);
    // Add your logic to handle the scroll position here
  };

  return (
    <div className="p-4">
      <ResponsiveMasonry
        className={"mt-10"}
        columnsCountBreakPoints={{ 300: 2, 500: 3, 700: 4, 900: 7 }}
      >
        <Masonry gutter="16px">
          {productImages.map((d, index) => (
            <div
              onClick={() => {
                navigate(`/popup?d=${encodeURIComponent(JSON.stringify(d))}`);
                handleClick();
              }}
              key={index}
              className="relative bg-white group transform transition-transform hover:scale-105 rounded-lg overflow-hidden shadow-md"
              style={{
                borderRadius: "8px", // Match the border radius of the real div
              }}
            >
              <div
                className="absolute top-0 left-0 right-0 bottom-0 flex flex-col justify-center items-center p-2 bg-opacity-80 backdrop-blur backdrop-filter opacity-0 transition-opacity group-hover:opacity-80"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  zIndex: 2,
                  borderRadius: "8px", // Match the border radius
                }}
              >
                <h3 className="products__price text-center text-gray-900">{d.name}</h3>
                <p className="products__price text-xl text-center font-semibold leading-5 text-gray-800">{d.price} Tsh</p>
              </div>

              <img
                src={d.photo}
                alt={d.name}
                className="w-full h-auto rounded-lg filter brightness-110"
                style={{
                  borderRadius: "8px", // Match the border radius
                  zIndex: 1,
                  filter: "brightness(1.12) contrast(1.02) saturate(1.1)",
                }}
              />
            </div>
          ))}
        </Masonry>
      </ResponsiveMasonry>

      {loadingMore && (
        <div className="flex justify-center mt-4">
          <ClockLoader color="#3B82F6" size={30} />
        </div>
      )}
    </div>
  );
}
*/

